import axios from "axios";

/**
 * 新版後端(package-plus-management-backend / Neil,Laravel 12)client。
 *
 * 與舊版 `vipAxios`(REACT_APP_API_URL)分開:歸還等流程正逐步遷到新版後端,
 * 新版走 Sanctum Bearer token、統一回應外殼 { code, status, details, replyTime }。
 *
 * base URL:REACT_APP_MGMT_API_URL(stage 走 mgmt-api-proxy 的 https *.run.app,
 * 避免 LIFF https 前端打 http 後端的 mixed-content)。
 */
const mgmtURL = process.env.REACT_APP_MGMT_API_URL;

export const mgmtAxios = axios.create({
  baseURL: `${mgmtURL}`,
});

// 新版後端統一回應外殼
export interface MgmtEnvelope<T> {
  code: string;
  status: string;
  details: T;
  replyTime: string;
}

// 錯誤 details 有兩種形態:單一物件 或 [{target,message}]。前端要兼容。
export interface MgmtErrorDetail {
  target: string;
  message: string;
  // FME / 業務錯誤會多帶這些
  fme_error_code?: string;
  fme_error_message?: string;
  error_code?: string;
}

/**
 * 從新版後端錯誤回應抽第一則可讀訊息(兼容 array / object 兩種 details 形態)。
 */
export const parseMgmtError = (error: any): string => {
  const details = error?.response?.data?.details;
  if (Array.isArray(details)) {
    return details[0]?.message ?? "發生錯誤,請稍後再試";
  }
  if (details && typeof details === "object") {
    return details.message ?? "發生錯誤,請稍後再試";
  }
  return error?.message ?? "發生錯誤,請稍後再試";
};

/**
 * 取新版後端會員 token(先前 LINE 登入換得,存 localStorage)。
 */
export const getMgmtMemberToken = (): string | null => {
  try {
    return localStorage.getItem("mgmt_member_token");
  } catch {
    return null;
  }
};

const setMgmtMemberToken = (token: string): void => {
  try {
    localStorage.setItem("mgmt_member_token", token);
  } catch {
    /* ignore */
  }
};

export interface MgmtMember {
  id: number;
  mobile_number: string | null;
  realname: string | null;
  nickname: string | null;
  email: string | null;
  line_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * 用 LINE idToken 登入新版後端,換 Sanctum member token(快取)並回會員資料。
 * 對應:POST /api/member/auth/line/login → details.{token, member}。
 */
export const loginMgmtByLine = async (
  idToken: string
): Promise<{ token: string; member: MgmtMember | null } | null> => {
  // 不 catch:讓呼叫端(Auth context)能依 401 判斷 LINE idToken 過期 → 重新 LIFF 登入
  // ⚠️ Neil 的回應 token 欄位是 access_token(不是 token)
  const { data } = await mgmtAxios.request<MgmtEnvelope<{ access_token: string; member: MgmtMember }>>({
    url: `/api/member/auth/line/login`,
    method: "POST",
    data: { id_token: idToken },
  });
  const token = data?.details?.access_token;
  if (token) {
    setMgmtMemberToken(token);
    return { token, member: data.details.member ?? null };
  }
  return null;
};

/**
 * 確保有 member token:有就回,沒有且給了 idToken 就登入換一個。
 */
export const ensureMgmtMemberToken = async (
  idToken?: string
): Promise<string | null> => {
  const existing = getMgmtMemberToken();
  if (existing) return existing;
  if (idToken) {
    try {
      const r = await loginMgmtByLine(idToken);
      return r?.token ?? null;
    } catch {
      return null;
    }
  }
  return null;
};
